import { NewValueParams } from "ag-grid-community";
import { ClientFilter, EntityFilter } from "@jsq/common-components";
import { AgGridReact } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { type ExtensionFieldObjectType } from "~/graphql/supergraphTypes";
import { SectionWithFields, TabWithSections } from "../../../CustomFields/types";
import { createInvestmentEntitiesGridOptions } from "../../utils/agGridUtils";
import {
  generateExtensionFieldColumns,
  createExtensionFieldColumnGroup,
  createSectionBasedColumnGroups,
  createTabBasedColumnGroups,
} from "./extensionFieldColumns";

/**
 * Base column definitions for Investment Entities Grid
 */
export const baseColumnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
  {
    field: "arena",
    headerName: "Client",
    sortable: true,
    flex: 3, // 30% width ratio
    minWidth: 150,
    maxWidth: 300,
    pinned: "left",
    resizable: true,
    wrapText: true, // Enable text wrapping
    autoHeight: true, // Allow row height to expand for wrapped text
    lockPosition: "left",
    cellStyle: {
      display: "flex",
      alignItems: "center",
      fontWeight: "400",
      fontSize: "14px",
      color: Colors.Neutral800,
      lineHeight: "1.4",
    },
    menuTabs: ["generalMenuTab"],
    filter: ClientFilter,
    filterParams: {
      multiselect: true,
    },
  },
  {
    field: "name",
    headerName: "Entity",
    sortable: true,
    flex: 4, // 40% width ratio
    minWidth: 200,
    maxWidth: 400,
    pinned: "left",
    resizable: true,
    wrapText: true, // Enable text wrapping
    autoHeight: true, // Allow row height to expand for wrapped text
    lockPosition: "left",
    cellStyle: {
      display: "flex",
      alignItems: "center",
      fontWeight: "400",
      fontSize: "14px",
      color: Colors.Neutral800,
      lineHeight: "1.4",
    },
    // ...createTextColumnFilter(), // Enable text filtering for name column
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab"],
    filter: EntityFilter,
    filterParams: {
      dependentFilter: "arena",
      multiselect: true,
    },
  },
];

/**
 * Grid options configuration for Investment Entities Grid
 * Uses shared utilities for consistent behavior across DataVault grids
 */
export const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] =
  createInvestmentEntitiesGridOptions();

// Generate column definitions including extension fields grouped by tabs or sections
export const useColumnDefinitions = (
  sectionsWithFields: SectionWithFields[],
  tabsWithSections: TabWithSections[],
  extensionFields: ExtensionFieldObjectType[],
  onCellValueChanged?: (params: NewValueParams) => void,
): AgGridReact.AgGridReactProps["columnDefs"] => {
  // Generate extension field columns with optional editing support
  const extensionFieldColumns = generateExtensionFieldColumns(
    extensionFields,
    onCellValueChanged,
  );

  // Create appropriate column groups based on whether tabs are enabled
  let extensionFieldGroups;

  if (tabsWithSections.length > 0) {
    // Use tab-based grouping when tabs are enabled
    extensionFieldGroups = createTabBasedColumnGroups(extensionFieldColumns, tabsWithSections);
  } else if (sectionsWithFields.length > 0) {
    // Fall back to section-based grouping
    extensionFieldGroups = createSectionBasedColumnGroups(
      extensionFieldColumns,
      sectionsWithFields,
    );
  } else {
    // No sections available, use simple grouping
    extensionFieldGroups = createExtensionFieldColumnGroup(extensionFieldColumns);
  }

  // Combine base columns with extension field columns
  const allColumns = [...baseColumnDefs];

  if (extensionFieldGroups) {
    if (Array.isArray(extensionFieldGroups)) {
      // Multiple groups (sections or tabs)
      allColumns.push(...extensionFieldGroups);
    } else {
      // Single group (fallback)
      allColumns.push(extensionFieldGroups);
    }
  }

  return allColumns;
};
